import MenuItem from '@mui/material/MenuItem';
import type { SelectProps as MuiSelectProps } from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Translate, type Translation } from '@shared/translation';
import type { ReactElement, ReactNode } from 'react';
import { roundedInputSx } from './roundedInput';
import { SelectSkeleton } from './SelectSkeleton';
import { TextField, type TextFieldProps } from './TextField';
import { toSxArray } from './utils';

type SelectValue = string | number;
type FieldValue = SelectValue | '';
type TextFieldSlotProps = NonNullable<TextFieldProps['slotProps']>;
type ObjectSlotProps<T> = Exclude<T, (...args: never[]) => unknown>;
type InputSlotProps = ObjectSlotProps<TextFieldSlotProps['input']> & {
  sx?: SxProps<Theme>;
};
type InputLabelSlotProps = ObjectSlotProps<TextFieldSlotProps['inputLabel']>;
type HtmlInputSlotProps = ObjectSlotProps<TextFieldSlotProps['htmlInput']>;
type FormHelperTextSlotProps = ObjectSlotProps<
  TextFieldSlotProps['formHelperText']
>;
type SelectSlotProps = {
  input?: InputSlotProps;
  inputLabel?: InputLabelSlotProps;
  htmlInput?: HtmlInputSlotProps;
  formHelperText?: FormHelperTextSlotProps;
  select?: Omit<
    Partial<MuiSelectProps<FieldValue>>,
    | 'children'
    | 'defaultValue'
    | 'displayEmpty'
    | 'multiple'
    | 'native'
    | 'onChange'
    | 'renderValue'
    | 'value'
    | 'variant'
  >;
};

export interface SelectOption {
  value: SelectValue;
  label?: Translation | ReactNode;
  disabled?: boolean;
}

type SelectTextFieldProps = Pick<
  TextFieldProps,
  | 'autoComplete'
  | 'autoFocus'
  | 'color'
  | 'disabled'
  | 'error'
  | 'fullWidth'
  | 'helperText'
  | 'hidden'
  | 'id'
  | 'inputRef'
  | 'label'
  | 'loading'
  | 'margin'
  | 'name'
  | 'onBlur'
  | 'onFocus'
  | 'placeholder'
  | 'required'
  | 'requiredLabel'
  | 'showRequiredLabel'
  | 'size'
  | 'sx'
  | 'variant'
>;

type SelectBaseProps = SelectTextFieldProps & {
  value?: FieldValue;
  defaultValue?: FieldValue;
  onChange?: MuiSelectProps<FieldValue>['onChange'];
  showEmptyOption?: boolean;
  emptyOptionLabel?: Translation | ReactNode;
  rounded?: boolean;
  slotProps?: SelectSlotProps;
};

export type DefaultSelectProps = SelectBaseProps & {
  options: SelectOption[];
  getOptionValue?: (option: SelectOption) => SelectValue;
  getOptionLabel?: (option: SelectOption) => Translation | ReactNode;
  renderOption?: (option: SelectOption) => ReactNode;
  renderValue?: (option: SelectOption | undefined) => ReactNode;
};

export type CustomSelectProps<T> = SelectBaseProps & {
  options: T[];
  getOptionValue: (option: T) => SelectValue;
  getOptionLabel: (option: T) => Translation | ReactNode;
  renderOption?: (option: T) => ReactNode;
  renderValue?: (option: T | undefined) => ReactNode;
};

export type SelectProps<T = SelectOption> =
  | DefaultSelectProps
  | CustomSelectProps<T>;

type NormalizedSelectProps<T> = SelectBaseProps & {
  options: T[];
  getOptionValue: (option: T) => SelectValue;
  getOptionLabel: (option: T) => Translation | ReactNode;
  renderOption?: (option: T) => ReactNode;
  renderValue?: (option: T | undefined) => ReactNode;
};

const roundedSelectSx: SxProps[] = [
  roundedInputSx,
  {
    '.MuiInputBase-input:focus': {
      backgroundColor: 'unset',
    },
    '.MuiSelect-select': {
      padding: 0,
    },
    '.MuiSelect-icon': {
      right: 12,
    },
  },
];

export function Select(props: DefaultSelectProps): ReactElement;
export function Select<T>(props: CustomSelectProps<T>): ReactElement;
export function Select(
  props: DefaultSelectProps | CustomSelectProps<unknown>
): ReactElement {
  if (hasCustomAccessors(props)) {
    return <SelectView {...props} />;
  }

  return (
    <SelectView
      {...props}
      getOptionValue={props.getOptionValue ?? defaultGetOptionValue}
      getOptionLabel={props.getOptionLabel ?? defaultGetOptionLabel}
    />
  );
}

function SelectView<T>({
  loading,
  margin = 'normal',
  options,
  showEmptyOption = false,
  emptyOptionLabel = '-',
  getOptionValue,
  getOptionLabel,
  renderOption,
  renderValue,
  rounded = false,
  placeholder,
  label,
  value,
  defaultValue,
  onChange,
  slotProps,
  sx,
  ...rest
}: NormalizedSelectProps<T>) {
  if (loading) {
    return (
      <SelectSkeleton
        hasLabel={Boolean(label)}
        margin={margin === 'none' ? 'none' : 'dense'}
        sx={toSxArray(sx)}
      />
    );
  }

  const inputSlotProps = slotProps?.input;
  const selectSlotProps = slotProps?.select;
  const selectOnChange = onChange as MuiSelectProps['onChange'];

  return (
    <TextField
      {...rest}
      margin={margin}
      sx={sx}
      label={label}
      placeholder={placeholder}
      value={value}
      defaultValue={defaultValue}
      select
      slotProps={{
        ...slotProps,
        input: {
          ...inputSlotProps,
          sx: [
            ...(rounded ? roundedSelectSx : []),
            ...toSxArray(inputSlotProps?.sx),
          ],
        },
        select: {
          displayEmpty: true,
          ...selectSlotProps,
          onChange: selectOnChange,
          renderValue: (value: unknown) => (
            <DefaultRenderValue
              value={value}
              options={options}
              getOptionValue={getOptionValue}
              getOptionLabel={getOptionLabel}
              renderOption={renderOption}
              renderValue={renderValue}
              placeholder={placeholder}
              hasLabel={Boolean(label)}
            />
          ),
        },
      }}
    >
      {renderSelectOptions({
        options,
        showEmptyOption,
        emptyOptionLabel,
        getOptionValue,
        getOptionLabel,
        renderOption,
      })}
    </TextField>
  );
}

function hasCustomAccessors(
  props: DefaultSelectProps | CustomSelectProps<unknown>
): props is CustomSelectProps<unknown> {
  return Boolean(props.getOptionValue && props.getOptionLabel);
}

function renderSelectOptions<T>({
  options,
  showEmptyOption,
  emptyOptionLabel,
  getOptionValue,
  getOptionLabel,
  renderOption,
}: {
  options: T[];
  showEmptyOption: boolean;
  emptyOptionLabel: Translation | ReactNode;
  getOptionValue: (option: T) => SelectValue;
  getOptionLabel: (option: T) => Translation | ReactNode;
  renderOption: ((option: T) => ReactNode) | undefined;
}) {
  return [
    ...(showEmptyOption
      ? [
          <MenuItem value="" key="empty">
            <Typography variant="body2">
              <Translate text={emptyOptionLabel} />
            </Typography>
          </MenuItem>,
        ]
      : []),
    ...options.map((option) => {
      const value = getOptionValue(option);

      return (
        <MenuItem
          value={value}
          key={value}
          disabled={getDefaultOptionDisabled(option)}
        >
          <Typography variant="body2">
            {renderOption?.(option) ?? renderLabel(getOptionLabel(option))}
          </Typography>
        </MenuItem>
      );
    }),
  ];
}

function DefaultRenderValue<T>({
  value,
  options,
  getOptionValue,
  getOptionLabel,
  renderOption,
  renderValue,
  placeholder,
  hasLabel,
}: {
  value: unknown;
  options: T[];
  getOptionValue: (option: T) => SelectValue;
  getOptionLabel: (option: T) => Translation | ReactNode;
  renderOption?: (option: T) => ReactNode;
  renderValue?: (option: T | undefined) => ReactNode;
  placeholder?: Translation;
  hasLabel: boolean;
}) {
  const option = options.find((option) => getOptionValue(option) === value);
  const showPlaceholder = placeholder && !option && !hasLabel;
  const valueContent = option
    ? (renderValue?.(option) ??
      renderOption?.(option) ??
      renderLabel(getOptionLabel(option)))
    : renderValue?.(undefined);

  return (
    <Typography
      variant="body2"
      sx={(theme) => ({
        ...(showPlaceholder && { color: theme.palette.text.disabled }),
      })}
      noWrap
    >
      {showPlaceholder && <Translate text={placeholder} />}
      {!showPlaceholder && valueContent}
    </Typography>
  );
}

function defaultGetOptionValue(option: unknown) {
  return (option as SelectOption).value;
}

function defaultGetOptionLabel(option: unknown) {
  return (option as SelectOption).label;
}

function getDefaultOptionDisabled(option: unknown) {
  return typeof option === 'object' && option !== null && 'disabled' in option
    ? Boolean((option as SelectOption).disabled)
    : undefined;
}

function renderLabel(label: Translation | ReactNode) {
  return isTranslation(label) ? <Translate text={label} /> : <>{label}</>;
}

function isTranslation(value: Translation | ReactNode): value is Translation {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    (typeof value === 'object' && 'message' in value)
  );
}
